/**
 * System API Integration builders
 *
 * The System API integrations differ between environments ONLY by their host
 * (production vs. localhost). Everything else — protocol, auth strategy, retry,
 * timeout, headers — is constant. So instead of storing full IntegrationConfig
 * objects in GlobalConfig (and duplicating them in the dev override), we keep the
 * constant template here in source and build each config from the configured
 * `systemApiHost` address at runtime.
 *
 * GlobalConfig therefore stores only the address (`systemApiHost`).
 */
import { IntegrationType, IntegrationAuthType } from '@rammetec/data-access';
/**
 * Known System API integrations, keyed by the GlobalConfig field name that
 * historically held the full object. These are now built from `systemApiHost`.
 */
export const SYSTEM_API_INTEGRATION_FIELDS = {
    systemApiIntegration: {
        id: 'systemApiIntegration',
        name: 'system-api',
        label: 'System API',
        pathSuffix: 'fb/v1/',
    },
    systemApiUserIntegration: {
        id: 'systemApiUserIntegration',
        name: 'system-api-user',
        label: 'System API - User Management',
        pathSuffix: 'fb/v1/users/',
    },
    systemApiOrgUserIntegration: {
        id: 'systemApiOrgUserIntegration',
        name: 'system-api-org-user',
        label: 'System API - Org User Management',
        pathSuffix: 'fb/v1/organizations/',
    },
};
/** Type guard: whether a GlobalConfig field is a known System API integration. */
export function isSystemApiIntegrationField(field) {
    return field in SYSTEM_API_INTEGRATION_FIELDS;
}
/**
 * Builds a fully-formed IntegrationConfig from the System API host and a definition.
 *
 * `host` is the REST/HTTP address (`systemApiHost`, e.g. `https://host`). The WS
 * address is a separate field (`systemApiHostWs`) consumed by the WS bootstrap.
 * A trailing slash on the host is tolerated; the path suffix is appended cleanly.
 */
export function buildSystemApiIntegration(host, def) {
    const base = host.trim().replace(/\/+$/, '');
    return {
        id: def.id,
        name: def.name,
        label: def.label,
        type: IntegrationType.REST,
        baseUrl: `${base}/${def.pathSuffix}`,
        auth: { type: IntegrationAuthType.SESSION_TOKEN },
        retry: { enabled: true, maxAttempts: 3, delayMs: 1000 },
        timeoutMs: 30000,
        defaultHeaders: { 'Content-Type': 'application/json' },
        enabled: true,
    };
}
//# sourceMappingURL=systemApiIntegrations.js.map